import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, X, Send } from "lucide-react";

export const SupportChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const openRef = useRef(open);
  openRef.current = open;

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await api.get("/support/messages");
      setMessages(data);
      setUnread(0);
    } catch {}
  }, []);

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/support/unread-count");
      setUnread(data.unread || 0);
    } catch {}
  }, []);

  useEffect(() => { loadUnread(); }, [loadUnread]);

  // Poll: messages every 8s while open, unread badge every 30s while closed
  useEffect(() => {
    const t = setInterval(() => (openRef.current ? loadMessages() : loadUnread()), openRef.current ? 8000 : 30000);
    return () => clearInterval(t);
  }, [open, loadMessages, loadUnread]);

  // Live pushes relayed from Layout's SSE stream
  useEffect(() => {
    const onMsg = (e) => {
      if (openRef.current) {
        setMessages((m) => [...m, e.detail]);
      } else {
        setUnread((c) => c + 1);
        toast("New reply from Crystal support", { description: "Open the chat to read it", duration: 5000 });
      }
    };
    window.addEventListener("support-message", onMsg);
    return () => window.removeEventListener("support-message", onMsg);
  }, []);

  useEffect(() => {
    if (open) loadMessages();
  }, [open, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/support/messages", { text: body });
      setMessages((m) => [...m, data]);
      setText("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Message failed to send");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed bottom-20 right-5 z-50 flex flex-col items-end">
      {open && (
        <div
          className="w-[340px] sm:w-[380px] h-[480px] mb-3 bg-white border border-slate-200 shadow-2xl flex flex-col"
          data-testid="support-chat-panel"
        >
          <div className="bg-slate-900 px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.2em] text-blue-300 font-bold uppercase">Crystal Support</div>
              <div className="text-xs text-slate-400 mt-0.5">Talk to our compliance team</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white" data-testid="support-chat-close">
              <X size={16} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
            {messages.length === 0 && (
              <div className="text-xs text-slate-500 text-center mt-8 px-6" data-testid="support-chat-empty">
                Have a question about a filing, document or anything else? Send us a message — our team replies right here.
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.sender_role === "client" ? "justify-end" : "justify-start"}`} data-testid="support-chat-message">
                <div className={`max-w-[80%] px-3 py-2 text-sm leading-snug ${
                  m.sender_role === "client"
                    ? "bg-blue-700 text-white"
                    : "bg-white border border-slate-200 text-slate-800"
                }`}>
                  {m.sender_role !== "client" && (
                    <div className="text-[10px] font-bold text-blue-700 uppercase tracking-wider mb-0.5">Crystal Team</div>
                  )}
                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                  <div className={`text-[9px] mt-1 ${m.sender_role === "client" ? "text-blue-200" : "text-slate-400"}`}>
                    {new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={send} className="border-t border-slate-200 p-2 flex gap-2 bg-white">
            <input
              className="hs-input flex-1 !py-2 text-sm"
              placeholder="Type your message…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="support-chat-input"
            />
            <button
              type="submit"
              disabled={sending || !text.trim()}
              className="hs-btn-primary px-3 disabled:opacity-50"
              data-testid="support-chat-send"
            >
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
      <div className="flex items-center gap-3">
        {!open && (
          <button
            onClick={() => setOpen(true)}
            className="bg-slate-900 text-white px-4 py-2.5 shadow-xl text-left hover:bg-slate-800 transition-colors"
            data-testid="support-chat-prompt"
          >
            <span className="block text-[13px] font-semibold leading-tight">Need help? Ask a question</span>
            <span className="block text-[10px] text-blue-300 uppercase tracking-wider mt-0.5">Talk to our compliance team</span>
          </button>
        )}
        <button
          onClick={() => setOpen((o) => !o)}
          className="relative w-14 h-14 bg-blue-700 hover:bg-blue-800 text-white flex items-center justify-center shadow-xl transition-colors flex-shrink-0"
          data-testid="support-chat-bubble"
          aria-label="Support chat"
        >
          {open ? <X size={22} /> : <MessageCircle size={22} />}
          {!open && unread > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="support-chat-unread">
              {unread}
            </span>
          )}
        </button>
      </div>
    </div>
  );
};
